import { toKpiNumberFormat, toNumberScale } from '../../dvm-layout-builder/dvm-builder-number-format.utils';

/**
 * Pure helpers that turn a compute response + tile format into the
 * horizontal-bar widget data. Used by the step at Generate time and by the
 * shell when a recompute result lands on an existing tile.
 */

/** The `horizontalBar` row list inside a compute response / responseList entry. */
export function extractHorizontalBarRows(source: unknown): Array<Record<string, unknown>> | null {
  const container = Array.isArray(source) ? (source[0] as Record<string, unknown>) : (source as Record<string, unknown>);
  const dataArr = container?.['data'] as Array<Record<string, unknown>> | undefined;
  const bar = dataArr?.[0]?.['horizontalBar'];
  return Array.isArray(bar) ? (bar as Array<Record<string, unknown>>) : null;
}

export interface HbWidgetDataContext {
  tileId: string;
  title: string;
  format?: Record<string, unknown>;
  /** Group-by axis title (the chart's Y axis). */
  yAxisName: string;
  /** Value axis title (the chart's X axis). */
  xAxisName: string;
}

/** Widget affixes from the Format popup: X (value axis) wraps the numbers,
 *  Y (group-by axis) wraps the category labels. */
export function hbAffixesFromFormat(format?: Record<string, unknown>): Record<string, string> {
  const str = (key: string): string => String(format?.[key] ?? '');
  return {
    valuePrefix: str('xAxisPrefix'),
    valueSuffix: str('xAxisSuffix'),
    categoryPrefix: str('yAxisPrefix'),
    categorySuffix: str('yAxisSuffix'),
  };
}

export function buildHbWidgetData(response: Record<string, unknown> | null, ctx: HbWidgetDataContext): Record<string, unknown> {
  const rows =
    extractHorizontalBarRows(response?.['responseList']) ??
    extractHorizontalBarRows(response) ??
    [];
  return {
    staticData: rows,
    categoryKey: 'department',
    valueKey: 'value',
    widgetId: 'dvm-tile-hbar-' + ctx.tileId,
    title: ctx.title.trim(),
    height: '100%',
    fullYAxisLabels: true,
    yAxisLabelWidth: 150,
    valueLabelPosition: 'inside',
    numberFormat: toKpiNumberFormat(ctx.format),
    numberScale: toNumberScale(ctx.format?.['numberFormat']),
    decimals: Number(ctx.format?.['decimals'] ?? 1),
    ...hbAffixesFromFormat(ctx.format),
    yAxisName: ctx.yAxisName || '',
    xAxisName: ctx.xAxisName || 'Value',
    showXAxis: true,
  };
}
